import React, { useState, useContext, useRef, useEffect } from 'react';

import ContextMenu from '../global/ContextMenu';
import MiniModal from  '@/components/modals/MiniModal';
import MoreDetails from './MoreDetails';
import FilterHeader from '../global/FilterHeader';
import NotFound from './NotFound';
import useOnScreen from  '@/components/hooks/useOnScreen';

import { LockerContextRefreshType as Table, ModalProps } from  '@/interfaces/LockerManagement';
import { IHttpRequest } from  '@/interfaces/IHttp';
import { container, TYPES } from  '@/inversifyconfig/container.config';
import { LockerManagementContext } from '@/context/LockerManagmentContext';
import { BuildPath } from  '@/helpers/Paths';
import { download, getFileTimeStamp } from  '@/helpers/Files';
import { ToastMessageType } from  '@/components/elements/global/ToastNotification';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Truncate } from  '@/helpers/Text';


const ArchivedManagementTable = ({setToast}:{setToast: (messageType: ToastMessageType, message: JSX.Element) => void}) => {
    /* Ref for infinite scrolling, if there are more files, load them and append to files when this comes into view */
    const scrollTo = useRef();
    const isVisible = useOnScreen(scrollTo, document.querySelector('.table-wrapper.archive'));

    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalContents, setModalContents] = useState<ModalProps>();

    const { archivedBatches, nextSkip, isLoading, searchParams, refreshData, nextPage, searchAndFilter,  } = useContext(LockerManagementContext)
    const httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest);  
    
    // scroll div is in view, and there is more data to retrieve
    useEffect(()=>{
        isVisible && archivedBatches.length && (archivedBatches.length === nextSkip) && nextPage(Table.ARCHIVE);
    },[isVisible, nextSkip])
    
    function generateContextOptions(fileName: string, id: number) {
        return [
            {
                label: 'More Details',
                onClick: () => showMoreDetailsModal(fileName, id)
            },
            {
                label: 'Restore Batch',
                onClick: () => showRestoreModal(fileName, id)
            }, 
        ]
    }


    function TableRows(): JSX.Element  {
        return (
        <>
            {archivedBatches.map((batch, idx) => (
                <tr key={`table-row-${idx}`}>
                    <td>
                        <span className="lockerfile-batchname">
                            <img src={`${BuildPath()}/images/locker/fileIcons.svg`} alt="batch icon"/>
                            {batch.batchName.length >= 15 &&
                            <ReactTooltip id={`tooltip-batchName-${batch.batchId}`} place="top" className='thrive-tooltip no-width'  aria-hidden="true">
                                <span>{batch.batchName}</span>
                            </ReactTooltip> 
                            }
                            <span data-tooltip-id={`tooltip-batchName-${batch.batchId}`}>
                                {Truncate(batch.batchName, 15)}
                            </span>
                        </span>
                    </td>
                    <td>{new Date(batch.lastArchiveActionDate).toLocaleDateString('en-gb')}</td>
                    <td>{batch.archivedFiles}</td>
                    <td>{batch.archivedFileUsers}</td>
                    <td>{batch.removedByUserName}</td>
                    <td className="more-details-button-wrapper">
                        <ContextMenu
                            className="more-details-button"
                            contextLabel={`More Details - ${batch.batchName}`}
                            options={generateContextOptions(batch.batchName, batch.batchId)}
                            closeOnScrollSelector={'.table-wrapper'}
                        />
                    </td>
                </tr>
            ))}
        </>
        );
    }
        

    function showRestoreModal (fileName: string, id: number) {
        setShowModal(true)
        setModalContents({
            title: `Restore Batch`,
            confirmLabel: `Yes, Restore Files`,
            onClose: () => setShowModal(false),
            onConfirm: () =>  restoreFile(fileName, id),
            contents: (
                <>
                    <h3 className="archive-sub-heading">Are you sure you want to restore <span className="emphasis-text">{Truncate(fileName, 50)}?</span></h3>
                    <div className="archive-warning-wrapper">
                        <img 
                            className="archive-warning-icon"
                            src={`${BuildPath()}/images/userupload/error.svg`}
                        />
                        <p className="archive-warning-text warning-text">
                            {
                                <>This will merge all batches with the same Batch ID</>
                            }
                        </p>
                    </div>
                    
                </>
            )
        });
    }

    function showMoreDetailsModal (fileName: string, id: number) {
        setShowModal(true);
        setModalContents({
            title: `More Details: ${Truncate(fileName, 50)}`,
            confirmLabel: 'Download Full Report',
            cancelLabel: 'Close',
            onClose: () => setShowModal(false),
            onConfirm: () => {
                download(
                    `../../api/locker/batches/${id}/details/download`,
                    `${fileName}_MoreDetailsReport_${getFileTimeStamp()}.csv`
                );
            },
            contents: <MoreDetails id={id} type="archive" />
        })
    }

    function restoreFile(fileName: string, id: number) {
        httpCall.put(`locker/batches/${id}/archive`, {isArchived: false})
        .then(() => setToast(ToastMessageType.SUCCESS, <><span className="emphasised-text">{Truncate(fileName, 50)}&nbsp;</span> has been successfully restored</>))
        .catch(() => setToast(ToastMessageType.WARNING, <><span className="emphasised-text">{Truncate(fileName, 50)}&nbsp;</span> could not be restored at this time</>))
        .finally(()=> {
            refreshData(Table.ARCHIVE);
            setShowModal(false);
        })
    }
    
    return (
    <>
        <div className='anti-floating-text-div' />
        <div className={`table-wrapper archive narrow-scroll-bar`}>
            <table className={`archive ${archivedBatches.length > 13 ? 'scroll-table': ''}`}>
                <thead>
                    <tr>
                        <FilterHeader 
                            columnNames={["Batch ID", "Removed Date", "Files", "Users", "Removed By"]}
                            setFilter={(filter) => searchAndFilter(Table.ARCHIVE, filter)}
                            currentFilters={searchParams[Table.ARCHIVE]}
                        />
                        <th>Details</th>
                    </tr> 
                </thead>
                <tbody className={isLoading ? ' loading' : ''}>
                {!!archivedBatches.length && <TableRows />}
                </tbody>
            </table>
            <div className={`next-page-wrapper`} ref={scrollTo} />
            {!archivedBatches.length &&
                <NotFound message="You have no archived locker files" table={Table.ARCHIVE} />
            }
            {showModal &&
                <MiniModal 
                    className='larger-mini-modal locker-management-modal'
                    showModal={showModal}
                    {...modalContents}
                >
                    {modalContents.contents}
                </MiniModal>
            }
        </div>
    </>
       
)}

export default ArchivedManagementTable